import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import api from '@/db/apiClient';
import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { AlertCircleIcon } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleCreateUserProfile, handleSSOSignInOrSignUp } from './authConfig';
import {
  AuthViewHeader,
  AuthViewTitle,
  AuthViewWrapper,
  AuthWrapper,
  LogoWrapper,
} from './AuthStyles';
import {
  closeProfileGate,
  openProfileGate,
  rejectProfileGate,
} from './profileReadyGate';
import logo from '/ui/new-spellcaster-logo.svg';

export default function AdminSignUp() {
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<ReactNode | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();

    if (!trimmedFirstName) {
      setError('Name is required.');
      return;
    }
    if (!trimmedEmail) {
      setError('Email is required.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }
    if (!passwordConfirm) {
      setError('Password confirmation is required.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      openProfileGate();
      const { user } = await createUserWithEmailAndPassword(
        api.asEducator.auth,
        trimmedEmail,
        password,
      );
      await handleCreateUserProfile(user, trimmedFirstName, trimmedLastName);
      closeProfileGate();
      await api.asEducator.generateVerificationCode({ uid: user.uid });
      trackMixpanelEvent('User Signed Up via Email');
      setLoading(false);
    } catch (error) {
      rejectProfileGate(error);
      console.error('Error signing up:', error);
      setLoading(false);
      const code =
        error && typeof error === 'object' && 'code' in error
          ? error.code
          : 'unknown';
      if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (code === 'auth/weak-password') {
        setError('Please enter a password that is at least 6 characters long.');
      } else if (code === 'auth/email-already-in-use') {
        setError(
          'This email is already in use. Please log in or use a different email address.',
        );
      } else {
        setError(UNKNOWN_ERROR_MESSAGE);
      }
    }
  }

  async function handleGoogleSignUp() {
    setError(null);
    setLoading(true);
    try {
      await handleSSOSignInOrSignUp();
      trackMixpanelEvent('User Signed In via SSO');
      setLoading(false);
    } catch (error) {
      console.error('Error during Google sign up:', error);
      setLoading(false);
      setError(UNKNOWN_ERROR_MESSAGE);
    }
  }

  return (
    <AuthViewWrapper>
      <AuthViewHeader>
        <LogoWrapper onClick={() => navigate('/')}>
          <img src={logo} alt="logo" />
        </LogoWrapper>
        <AuthViewTitle>Educator Sign Up</AuthViewTitle>

        <span className="text-sm mt-2 mb-4 text-muted-foreground">
          Looking for learner sign in?{' '}
          <b
            className="cursor-pointer hover:underline text-primary font-semibold"
            onClick={() => navigate('/game')}
          >
            Click here
          </b>
        </span>
      </AuthViewHeader>

      <AuthWrapper onSubmit={handleSubmit}>
        {error && (
          <Alert variant="destructive">
            <AlertCircleIcon size={16} className="ml-2" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex gap-3 w-full">
          <Input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First Name"
            disabled={isLoading}
          />
          <Input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last Name"
            disabled={isLoading}
          />
        </div>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          disabled={isLoading}
        />
        <div className="relative w-full">
          <Input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            disabled={isLoading}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 hover:text-foreground"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeSlashIcon className="size-4" />
            ) : (
              <EyeIcon className="size-4" />
            )}
          </button>
        </div>
        <div className="relative w-full">
          <Input
            type={showPasswordConfirm ? 'text' : 'password'}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            placeholder="Confirm Password"
            disabled={isLoading}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 hover:text-foreground"
            onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
            tabIndex={-1}
          >
            {showPasswordConfirm ? (
              <EyeSlashIcon className="size-4" />
            ) : (
              <EyeIcon className="size-4" />
            )}
          </button>
        </div>
        <Button type="submit" loading={isLoading} className="w-full mt-2">
          Sign Up
        </Button>
        <Button
          type="button"
          variant="link"
          disabled={isLoading}
          className="p-0 h-fit"
          onClick={() => navigate('/login')}
        >
          Already have an account? Sign in
        </Button>

        <div className="flex items-center gap-3 w-full my-4">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">OR</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Button
          type="button"
          variant="outline"
          disabled={isLoading}
          className="w-full"
          onClick={handleGoogleSignUp}
        >
          <img src="/icons/google.svg" alt="Google" className="size-4 mr-2" />
          Continue with Google
        </Button>
      </AuthWrapper>
    </AuthViewWrapper>
  );
}
